import { ChatResponse, SSEEvent, CVIResponse, CVIMessage, CVISelectionState } from '../types';

export interface CheckpointModifications {
  confirmation_id: string;
  selected_strategy_id?: string;
  modified_weights?: Record<string, number>;
  excluded_regions?: string[];
  context_overrides?: Record<string, Record<string, any>>;
  batch_overrides?: Record<string, Record<string, any>>;
  comparison_filters?: Record<string, any>;
  national_build_target?: number;
  policy_toggles?: Record<string, boolean>;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';


class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...options.headers },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (response.status === 204) return {} as T;
    return response.json();
  }

  async fetchScenarios(): Promise<any[]> {
    return this.fetch<any[]>('/scenarios');
  }

  async fetchScenario(scenarioId: string): Promise<any> {
    return this.fetch<any>(`/scenarios/${scenarioId}`);
  }

  async fetchAllocations(status?: string, scenarioId?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (status)     params.append('status', status);
    if (scenarioId) params.append('scenario_id', scenarioId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    try {
      return await this.fetch<any[]>(`/allocations${qs}`);
    } catch (error: any) {
      if (error.message.includes('404')) return [];
      throw error;
    }
  }

  async fetchLatestAllocation(status?: string, scenarioId?: string): Promise<any | null> {
    const list = await this.fetchAllocations(status, scenarioId);
    return list.length > 0 ? list[0] : null;
  }

  async getAllocation(runId: string): Promise<any | null> {
    try { return await this.fetch<any>(`/allocations/${runId}`); }
    catch (error: any) {
      if (error.message.includes('404')) return null;
      throw error;
    }
  }

  async approveAllocation(runId: string): Promise<any> {
    return this.fetch<any>(`/allocations/${runId}/approve`, { method: 'POST' });
  }

  async rejectAllocation(runId: string): Promise<any> {
    return this.fetch<any>(`/allocations/${runId}/reject`, { method: 'POST' });
  }

  async sendChatMessage(message: string, context?: string, pendingScenarioId?: string): Promise<ChatResponse> {
    return this.fetch<ChatResponse>('/chat', {
      method: 'POST',
      body: JSON.stringify({ message, context, pending_scenario_id: pendingScenarioId }),
    });
  }

  async sendCVIMessage(messages: CVIMessage[], selection: CVISelectionState, debug = false): Promise<CVIResponse> {
    return this.fetch<CVIResponse>('/cvi/chat', {
      method: 'POST',
      body: JSON.stringify({ messages, selection, debug }),
    });
  }

  streamScenarioRun(
    scenarioId: string,
    weights: Record<string, number> | null,
    globalSupplyOverride: number | null,
    globalDemandOverride: number | null,
    onEvent: (event: SSEEvent) => void,
    onError: (error: Error) => void,
    onComplete: () => void,
    parameterOverrides?: Record<string, number> | null,
    regionalOverrides?: Record<string, any> | null,
    strategyId?: string | null,
    batchOverrides?: Record<string, Record<string, number>> | null,
    comparisonFilters?: Record<string, any> | null
  ): () => void {
    let url = `${this.baseUrl}/stream/run/${scenarioId}`;
    const params = new URLSearchParams();

    if (weights) Object.entries(weights).forEach(([k, v]) => params.append(k, v.toString()));
    if (globalSupplyOverride) params.append('supply', globalSupplyOverride.toString());
    if (globalDemandOverride) params.append('demand', globalDemandOverride.toString());
    if (parameterOverrides) {
      if (parameterOverrides.projected_days_to_sell != null) params.append('days_to_sell', parameterOverrides.projected_days_to_sell.toString());
      if (parameterOverrides.transport_cost != null)         params.append('transport_cost', parameterOverrides.transport_cost.toString());
      if (parameterOverrides.holding_cost_per_day != null)   params.append('holding_cost', parameterOverrides.holding_cost_per_day.toString());
    }
    if (regionalOverrides) params.append('regional_overrides', JSON.stringify(regionalOverrides));
    if (strategyId)        params.append('strategy_id', strategyId);
    if (batchOverrides)    params.append('batch_overrides', JSON.stringify(batchOverrides));
    if (comparisonFilters) {
      params.append('comparison_filters', JSON.stringify(comparisonFilters));
    }

    const qs = params.toString();
    if (qs) url = `${url}?${qs}`;

    const eventSource = new EventSource(url, { withCredentials: true });
    let isComplete = false;

    eventSource.onmessage = (event) => {
      try {
        const data: SSEEvent = JSON.parse(event.data);
        onEvent(data);
        if (data.type === 'complete' || data.type === 'error' || data.type === 'await_confirmation') {
          isComplete = true;
          eventSource.close();
          onComplete();
        }
      } catch (e) { console.error('Failed to parse SSE event:', e); }
    };

    eventSource.onerror = () => {
      if (!isComplete) { onError(new Error('SSE connection error')); eventSource.close(); onComplete(); }
    };

    return () => { eventSource.close(); };
  }

  streamConfirmCheckpoint(
    scenarioId: string,
    modifications: CheckpointModifications,
    onEvent: (event: SSEEvent) => void,
    onError: (error: Error) => void,
    onComplete: () => void
  ): () => void {
    const url = `${this.baseUrl}/stream/run/${scenarioId}/confirm`;
    const controller = new AbortController();
    let isComplete = false;

    const processSseLines = (raw: string) => {
      for (const line of raw.split('\n')) {
        if (!line.startsWith('data: ')) continue;
        try {
          const data: SSEEvent = JSON.parse(line.slice(6));
          onEvent(data);
          if (data.type === 'complete' || data.type === 'error' || data.type === 'await_confirmation') isComplete = true;
        } catch (e) { console.error('Failed to parse SSE event:', e); }
      }
    };

    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(modifications),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          processSseLines(lines.join('\n'));
        }
        if (buffer.trim()) processSseLines(buffer);
        if (!isComplete) onError(new Error('Stream ended before terminal event'));
        onComplete();
      })
      .catch((error) => {
        if (!isComplete && error.name !== 'AbortError') onError(error);
        onComplete();
      });

    return () => { controller.abort(); };
  }

  async confirmCheckpoint(modifications: CheckpointModifications): Promise<any> {
    return this.fetch<any>('/confirm', { method: 'POST', body: JSON.stringify(modifications) });
  }
}

export const apiService    = new ApiService();
export const useApiService = () => apiService;
export default apiService;
